import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EntityNameType } from '@core/interfaces/entity-name-type';
import { ApplicationAccessService } from '@core/services/application-access/application-access.service';
import { FeatureFlagsService } from '@core/services/feature-flags/feature-flags.service';
import { LoadEntityNames } from '@core/store/entity-names/entity-names.actions';
import { GetUserApi } from '@core/store/user/user.actions';
import { UserState } from '@core/store/user/user.state';
import { LoadingService } from '@shared/components/hub-loading-spinner/loading.service';
import {
  AccountStore,
  bindRouterToHost,
  type FlagsSnapshot,
  type InstalledAppsSnapshot,
} from '@ol/kit';
import { Store } from '@ngxs/store';
import { filter, switchMap, take, timer } from 'rxjs';

const ENTITY_NAMES_LOAD_DELAY_MS = 5000;

// Embed-mode root component for Designer. Renders the real Designer layout via
// the inner router-outlet and triggers the same store initialization that
// AppComponent runs in standalone mode, but skips the Auth0-specific error
// subscription — Hub owns auth flow when embedded.
@Component({
  selector: 'hub-designer-embed-root',
  template: `
    <router-outlet />
    <hub-loading-spinner [mainLoader]="true" />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DesignerEmbedRoot implements OnInit {
  private readonly store = inject(Store);
  private readonly loadingService = inject(LoadingService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accountStore = inject(AccountStore);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly applicationAccessService = inject(ApplicationAccessService);

  // The host shell hands its flags snapshot down as a custom-element property
  // (see PROP_KEYS in main.ts). Element property setters are synchronous, so
  // the service is seeded before any flag-gated route can be activated; a late
  // snapshot (slow network in the shell) is forwarded too — the service
  // decides whether it may still be adopted.
  // A signal input would deliver the snapshot via an effect on the next CD
  // tick; the embed contract requires the seed to land synchronously with the
  // property set, hence the legacy setter input.
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  set featureFlags(snapshot: FlagsSnapshot | undefined) {
    if (snapshot) {
      this.featureFlagsService.seedFlags(snapshot);
    }
  }

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  set installedApps(snapshot: InstalledAppsSnapshot | undefined) {
    if (snapshot) {
      this.applicationAccessService.seedInstalledApps(snapshot);
    }
  }

  ngOnInit(): void {
    // No initial navigation here: bindHostRouteToRouter (main.ts) drives the
    // router from the shell's `route` prop, which is already stripped of the
    // mount path and — unlike window.location.pathname — keeps the query string.
    this.loadingService.show();
    this.store.dispatch(new GetUserApi());

    // @ol/kit's AccountStore loaded the user during bootstrap, before the NGXS
    // user existed (GetUserApi resolves asynchronously above). Re-pull it into
    // the kit once the host user is available so <ol-user-dropdown> renders the
    // real identity instead of the empty profile it fell back to. Mirrors
    // AppComponent — the embed root replaces it in federation mode.
    this.store
      .select(UserState.user)
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this.accountStore.loadUser()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    timer(ENTITY_NAMES_LOAD_DELAY_MS)
      .pipe(take(1))
      .subscribe(() => {
        this.store.dispatch([
          new LoadEntityNames(EntityNameType.Connection),
          new LoadEntityNames(EntityNameType.Module),
          new LoadEntityNames(EntityNameType.Project),
        ]);
      });
    bindRouterToHost(this.router, this.destroyRef, 'designer-root');
  }
}
